/**
 * SoW-stage display-name → backend sowStatus groupings — single source for:
 *
 *  - eip-server controllers/admin/gcOutcome.ts (getRegionStats): derives the
 *    R28 dashboard SoW bucket map from SOW_STAGE_GROUPS so each stat card
 *    counts exactly the rows its deep-linked All SoWs list filter shows;
 *  - eip-client utilities/stageRegistry.ts: sources the backendValues of the
 *    corresponding registry entries from SOW_STAGE_ENTRY_BACKEND_VALUES, so
 *    the grouping cannot drift between the count (server) and the list
 *    filter (client).
 *
 * The client registry keeps role-scoped display metadata locally, and several
 * registry entries share one admin display name (the audit sub-states and
 * their deprecated "… 2" variants all render as "SoW Awaiting IPA review").
 * The registry therefore consumes the PER-ENTRY arrays below; the flattened
 * display-name table is composed FROM them — never the reverse.
 */

// ── Per-registry-entry backend values ───────────────────────────────────────
// Keys mirror the stageRegistry entry ids they feed (camelCased).
export const SOW_STAGE_ENTRY_BACKEND_VALUES = {
  // stageRegistry "sow-requested"
  sowRequested: ['sowRequested', 'New SoW Request'],
  // stageRegistry "sow-awaiting-ipa-review"
  sowAwaitingIpaReview: ['sowAwaitingApproval', 'sowAwaitingAdminApproval', 'Awaiting Expert Approval'],
  // stageRegistry "audit-clarification-needed" / "audit-update-needed"
  auditClarificationNeeded: ['Clarification Needed'],
  auditUpdateNeeded: ['Update Needed'],
  // stageRegistry deprecated legacy variants (kept countable on the server)
  deprecatedClarificationNeeded2: ['Clarification Needed 2'],
  deprecatedUpdateNeeded2: ['Update Needed 2'],
  // stageRegistry "sow-awaiting-ao-review"
  sowAwaitingAoReview: ['Awaiting Admin Approval'],
  // stageRegistry "sow-ready-for-audit"
  sowReadyForAudit: ['sowReadyForAudit', 'Ready for Audit'],
  // stageRegistry deprecated "deprecated-ready-for-audit-2"
  deprecatedReadyForAudit2: ['Ready for Audit 2'],
  // stageRegistry "sow-ready-for-signature"
  sowReadyForSignature: ['sowAwaitingSignature', 'Approved & Ready for Signatures'],
  // stageRegistry "sow-signed"
  sowSigned: ['sowSigned', 'Ready to Begin Work'],
} as const satisfies Record<string, readonly string[]>;

// ── Display-name grouping (the R28 dashboard buckets) ───────────────────────
// Each admin display name groups every backend value rendered under that
// label — including the audit sub-states and deprecated legacy variants.
export const SOW_STAGE_GROUPS = {
  'SoW Requested': [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowRequested],
  'SoW Awaiting IPA review': [
    ...SOW_STAGE_ENTRY_BACKEND_VALUES.sowAwaitingIpaReview,
    ...SOW_STAGE_ENTRY_BACKEND_VALUES.auditClarificationNeeded,
    ...SOW_STAGE_ENTRY_BACKEND_VALUES.auditUpdateNeeded,
    ...SOW_STAGE_ENTRY_BACKEND_VALUES.deprecatedClarificationNeeded2,
    ...SOW_STAGE_ENTRY_BACKEND_VALUES.deprecatedUpdateNeeded2,
  ],
  'SoW Awaiting AO Review': [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowAwaitingAoReview],
  'SoW Ready for Audit': [
    ...SOW_STAGE_ENTRY_BACKEND_VALUES.sowReadyForAudit,
    ...SOW_STAGE_ENTRY_BACKEND_VALUES.deprecatedReadyForAudit2,
  ],
  'SoW Ready for Signature': [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowReadyForSignature],
  'SoW Signed': [...SOW_STAGE_ENTRY_BACKEND_VALUES.sowSigned],
} satisfies Record<string, readonly string[]>;

export type SowStageGroupName = keyof typeof SOW_STAGE_GROUPS;
